import { forwardRef } from 'react'
import type { ComponentPropsWithoutRef } from 'react'
import classNames from 'classnames'
import { RadioGroup } from 'radix-ui'

type BreakoutRadioGroupProps = ComponentPropsWithoutRef<typeof RadioGroup.Root>

export const BreakoutRadioGroup = forwardRef<
  HTMLDivElement,
  BreakoutRadioGroupProps
>(function BreakoutRadioGroup({ className, ...props }, ref) {
  return <RadioGroup.Root ref={ref} className={className} {...props} />
})

type BreakoutRadioButtonProps = ComponentPropsWithoutRef<
  typeof RadioGroup.Item
> & {
  kind?: 'primary' | 'secondary' | 'tertiary'
  label: string | React.ReactNode | null
  variant?: 'default' | 'compact' | 'bare'
  'data-testid'?: string
}

export const BreakoutRadioButton = forwardRef<
  HTMLButtonElement,
  BreakoutRadioButtonProps
>(function BreakoutRadioButton(
  {
    label,
    disabled,
    className,
    kind = 'secondary',
    variant = 'default',
    'data-testid': testId,
    ...rest
  },
  ref
) {
  const isCompact = variant === 'compact'
  const isBare = variant === 'bare'

  return (
    <RadioGroup.Item
      ref={ref}
      disabled={disabled}
      data-testid={testId}
      className={
        isBare
          ? 'focus-ring-floating flex items-center gap-3 text-left'
          : classNames(
              'focus-ring-floating flex w-full flex-1 items-center gap-3 rounded-2xl text-left',
              {
                'border-2 px-4 py-2': isCompact,
                'h-[52px] px-4 py-1': !isCompact,

                'data-[state=checked]:border-fixed-accent-color': true,
                'data-[state=unchecked]:border-outline-light': true,

                'bg-core-primary text-core-on-primary': kind === 'primary',
                'bg-core-secondary text-core-on-secondary':
                  kind === 'secondary',
                'bg-core-tertiary text-core-on-tertiary': kind === 'tertiary',

                'cursor-not-allowed opacity-50': disabled,
                'cursor-pointer': !disabled,
              },
              className
            )
      }
      {...rest}
    >
      {/* Radio indicator */}
      <div className="relative flex h-[15px] w-[15px] min-w-[15px] items-center justify-center rounded-full border border-on-surface-disabled bg-transparent data-[state=checked]:border-fixed-accent-color">
        <RadioGroup.Indicator className="flex items-center justify-center">
          <div className="h-[8px] w-[8px] rounded-full bg-fixed-accent-color" />
        </RadioGroup.Indicator>
      </div>
      {!isBare &&
        label !== null &&
        (typeof label === 'string' ? (
          <strong className="text-label-medium">{label}</strong>
        ) : (
          label
        ))}
    </RadioGroup.Item>
  )
})
